/**
 * Lodestone Chart utilities
 * Provides reusable chart components using Chart.js
 */

import type { Chart as ChartJS, ChartConfiguration, TooltipItem } from 'chart.js';
import type { LngLat, ElevationData, ElevationChartOptions, WeightData, WeightChartOptions, HoverData } from './types';
import { METERS_PER_MILE, METERS_PER_KM, FEET_PER_METER } from './constants';
import { escapeHtml } from './utils';
import { COLORS } from './colors';

interface ProcessedElevationData {
    distances: number[];
    elevations: number[];
    coordinates: LngLat[];
    minElevation: number; // meters, so formatElevation converts exactly once
    maxElevation: number; // meters
}

/**
 * ElevationChart - Interactive elevation profile with map hover integration
 */
export class ElevationChart {
    #chart: ChartJS | null = null;
    #canvas: HTMLCanvasElement | null;
    #data!: ProcessedElevationData;
    #options!: ElevationChartOptions;
    #onHover: ((data: HoverData | null) => void) | null = null;

    constructor(canvasId: string, data: ElevationData, options: ElevationChartOptions = {}) {
        this.#canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
        if (!this.#canvas) {
            console.error('ElevationChart: canvas not found:', canvasId);
            return;
        }

        this.#options = {
            useMetric: false,
            onHover: undefined,
            ...options
        };
        this.#onHover = this.#options.onHover || null;

        this.#processData(data);
        this.#render();
    }

    /**
     * Process and prepare the elevation data
     */
    #processData(data: ElevationData): void {
        const { distances, elevations, coordinates, isOutAndBack } = data;

        // Convert units
        let distancesConverted = this.#options.useMetric
            ? distances.map(d => d / METERS_PER_KM) // km
            : distances.map(d => d / METERS_PER_MILE); // miles

        // Min/max stay in meters (mirroring an out-and-back leg cannot change them)
        const validMeters = elevations.map(e => e || 0).filter(e => e > 0);

        let elevationsConverted = this.#options.useMetric
            ? elevations.map(e => e || 0) // meters
            : elevations.map(e => (e || 0) * FEET_PER_METER); // feet

        let coords: LngLat[] = coordinates || [];

        // Mirror data for out-and-back routes
        if (isOutAndBack && distancesConverted.length > 0) {
            const totalOneWay = distancesConverted[distancesConverted.length - 1];
            const returnDistances = distancesConverted.slice(0, -1).reverse()
                .map(d => totalOneWay + (totalOneWay - d));
            const returnElevations = elevationsConverted.slice(0, -1).reverse();

            distancesConverted = [...distancesConverted, ...returnDistances];
            elevationsConverted = [...elevationsConverted, ...returnElevations];

            if (coords.length > 0) {
                const returnCoords = coords.slice(0, -1).reverse();
                coords = [...coords, ...returnCoords];
            }
        }

        this.#data = {
            distances: distancesConverted,
            elevations: elevationsConverted,
            coordinates: coords,
            minElevation: validMeters.length > 0 ? Math.min(...validMeters) : 0,
            maxElevation: validMeters.length > 0 ? Math.max(...validMeters) : 0
        };
    }

    /**
     * Get the min/max elevation in meters (pass them to formatElevation for display)
     */
    getElevationRange(): { min: number; max: number } {
        return {
            min: this.#data.minElevation,
            max: this.#data.maxElevation
        };
    }

    /**
     * Render the chart
     */
    #render(): void {
        if (!this.#canvas || typeof Chart === 'undefined') {
            console.error('ElevationChart: Chart.js not loaded');
            return;
        }

        const ctx = this.#canvas.getContext('2d');
        if (!ctx) return;

        const distanceUnit = this.#options.useMetric ? 'km' : 'mi';
        const elevationUnit = this.#options.useMetric ? 'm' : 'ft';
        const distanceLabel = this.#options.useMetric ? 'Distance (km)' : 'Distance (mi)';
        const elevationLabel = this.#options.useMetric ? 'Elevation (m)' : 'Elevation (ft)';

        const coordinates = this.#data.coordinates;
        const onHover = this.#onHover;

        const config: ChartConfiguration<'line', number[], string> = {
            type: 'line',
            data: {
                labels: this.#data.distances.map(d => d.toFixed(1)),
                datasets: [{
                    label: `Elevation (${elevationUnit})`,
                    data: this.#data.elevations,
                    fill: true,
                    backgroundColor: COLORS.primaryLight,
                    borderColor: COLORS.primary,
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: 6,
                    pointHoverBackgroundColor: COLORS.primary,
                    pointHoverBorderColor: 'white',
                    pointHoverBorderWidth: 2,
                    tension: 0.3
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    intersect: false,
                    mode: 'index'
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: COLORS.tooltip,
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        padding: 12,
                        displayColors: false,
                        callbacks: {
                            title: (items: TooltipItem<'line'>[]) => `${distanceUnit === 'mi' ? 'Mile' : 'Km'} ${items[0].label}`,
                            label: (item: TooltipItem<'line'>) => `Elevation: ${Math.round(item.raw as number).toLocaleString()} ${elevationUnit}`
                        }
                    }
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: distanceLabel,
                            color: COLORS.text,
                            font: { size: 11 }
                        },
                        ticks: {
                            color: COLORS.text,
                            maxTicksLimit: 8
                        },
                        grid: { display: false }
                    },
                    y: {
                        title: {
                            display: true,
                            text: elevationLabel,
                            color: COLORS.text,
                            font: { size: 11 }
                        },
                        ticks: {
                            color: COLORS.text,
                            callback: (val) => (val as number).toLocaleString()
                        },
                        grid: { color: COLORS.grid }
                    }
                },
                onHover: (_event, elements) => {
                    if (onHover && elements.length > 0) {
                        const index = elements[0].index;
                        if (index < coordinates.length) {
                            const coord = coordinates[index];
                            onHover({ index, lngLat: [coord[0], coord[1]] });
                        }
                    } else if (onHover) {
                        onHover(null);
                    }
                }
            }
        };

        this.#chart = new Chart(ctx, config);
    }

    /**
     * Destroy the chart
     */
    destroy(): void {
        if (this.#chart) {
            this.#chart.destroy();
            this.#chart = null;
        }
    }
}

/**
 * WeightChart - Doughnut chart for gear weight breakdown
 */
export class WeightChart {
    #chart: ChartJS | null = null;
    #canvas: HTMLCanvasElement | null;

    constructor(canvasId: string, data: WeightData, options: WeightChartOptions = {}) {
        this.#canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
        if (!this.#canvas) {
            console.error('WeightChart: canvas not found:', canvasId);
            return;
        }

        this.render(data, options);
    }

    /**
     * Render or update the chart
     */
    render(data: WeightData, options: WeightChartOptions = {}): void {
        if (!data || !data.categories || data.categories.length === 0) {
            return;
        }

        // Destroy existing chart
        if (this.#chart) {
            this.#chart.destroy();
        }

        const unit = options.unit || 'kg';
        const totalWeight = data.totalWeight || data.weights.reduce((a, b) => a + b, 0);

        const ctx = this.#canvas?.getContext('2d');
        if (!ctx) return;

        const config: ChartConfiguration<'doughnut', number[], string> = {
            type: 'doughnut',
            data: {
                labels: data.categories,
                datasets: [{
                    data: data.weights,
                    backgroundColor: COLORS.categories.slice(0, data.categories.length),
                    borderWidth: 0,
                    hoverOffset: 4
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                cutout: '60%',
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (context: TooltipItem<'doughnut'>) => {
                                const value = parseFloat(String(context.raw));
                                const percent = totalWeight > 0 ? ((value / totalWeight) * 100).toFixed(1) : '0.0';
                                return `${context.label}: ${value.toFixed(2)} ${unit} (${percent}%)`;
                            }
                        }
                    }
                }
            }
        };

        this.#chart = new Chart(ctx, config);

        // Render legend if container specified
        if (options.legendContainerId) {
            this.#renderLegend(options.legendContainerId, data, unit, totalWeight);
        }
    }

    /**
     * Render custom legend
     */
    #renderLegend(containerId: string, data: WeightData, unit: string, totalWeight: number): void {
        const container = document.getElementById(containerId);
        if (!container) return;

        container.innerHTML = data.categories.map((cat, i) => {
            const weight = data.weights[i];
            const percent = totalWeight > 0 ? ((weight / totalWeight) * 100).toFixed(1) : '0.0';
            return `
                <div class="flex items-center gap-2 p-3 rounded-xl bg-base-200 border border-base-300">
                    <div class="w-3 h-3 rounded-full shrink-0" style="background-color: ${COLORS.categories[i]}"></div>
                    <div class="flex-1 min-w-0">
                        <div class="font-bold text-base-content truncate">${escapeHtml(cat)}</div>
                        <div class="text-base-content/60 text-xs">${weight.toFixed(2)} ${unit} (${percent}%)</div>
                    </div>
                </div>
            `;
        }).join('');
    }

    /**
     * Destroy the chart
     */
    destroy(): void {
        if (this.#chart) {
            this.#chart.destroy();
            this.#chart = null;
        }
    }
}
